import {
  Card,
  Grid,
  Stack,
  TextField,
  FormControlLabel,
  Button,
  Checkbox,
  Avatar,
} from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ToppingsCategoryItem as IToppingsCategoryItem,
  ImageItems,
} from '../../../store/toppingsCategoryItem/api/interface';
import { FormikProvider, useFormik } from 'formik';
import {
  createToppingsCategoryItem,
  getToppingsCategoryItem,
  patchToppingsCategoryItem,
} from '../../../store/toppingsCategoryItem/api';
import { setStatus } from '../../../store/toppingsCategoryItem/toppingsCategoryItemSlice';
import { useEffect, useState, useRef } from 'react';
import { decryptString, getAssetURl } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { omit, uniqBy } from 'lodash';
import toppingsCategoryItemSchema from '../../../validation/toppingsCategoryItemSchema';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import DetailsForm from './DetailsForm';
import {
  findSelectToppingsCategory,
  searchSelectToppingsCategory,
} from '../../../store/selectToppingCategory/api';
import {
  setSkip as setSkipToppingsCategory,
  reset as resetToppingsCategory,
} from '../../../store/selectToppingCategory/selectToppingCategorySlice';
import MultipleAutoComplete from '../../../components/MultipleAutoComplete';
import { fillToppingCategoryItemState } from '../../../utils/fillToppingsCategoryItemData';
import {
  setStatus as selectToppingsCategoryItemSetStatus,
  reset as resetSelectToppingsCategoryItemReset,
} from '../../../store/selectToppingCategoryItem/selectToppingCategoryItemSlice';
import { uploadAssets } from '../../../utils/uploadAssets';
import { DocumentType } from '../../../enum';

const FlavorForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: any = useParams();
  const location = useLocation();
  const state = location.state as IToppingsCategoryItem; // Type Casting, then you can get the params passed via router

  const isAddMode = !params?.id;
  const { isLoading } = useAppSelector((state) => state.toppingsCategoryItem);
  const { accessToken } = useAppSelector((state) => state.auth);
  const [response, setResponse] = useState<any>();
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  // State for multi-category selection (all categories including primary)
  const [allToppingCategories, setAllToppingCategories] = useState<any[]>([]);
  const hasTriggeredRefresh = useRef(false);

  const formik = useFormik({
    initialValues: {
      id: '',
      toppingCategoryId: '',
      additionalToppingCategoryIds: [] as string[],
      labelEn: '',
      labelAr: '',
      code: '',
      descriptionEn: '',
      descriptionAr: '',
      previewUrl: '',
      position: '',
      price: '',
      discountedPrice: '',
      effortScore: 10,
      deliveryDayGap: '0',
      // isNew: true,
      hasColor: false,
      isBig: false,
      activeState: true,
      shapeItems: [
        {
          shapeId: '',
          // sliced: '',
          top: '',
          front: '',
        },
      ],
    },
    validationSchema: toppingsCategoryItemSchema,
    onSubmit: async (values) => {
      try {
        if (isAddMode) {
          const reqBody = omit(values, ['id']);
          // Remove computed fields
          delete (reqBody as any).additionalCategories;
          delete (reqBody as any).allToppingCategoryIds;
          reqBody.shapeItems = reqBody.shapeItems.filter(
            (item: ImageItems) => item.top && item.front
          );
          await dispatch(createToppingsCategoryItem(reqBody)).unwrap();
        }
        if (!isAddMode) {
          const reqBody: any = values;
          delete reqBody.createdAt;
          delete reqBody.createdBy;
          delete reqBody.updatedAt;
          delete reqBody.updatedBy;
          // Remove computed fields
          delete reqBody.additionalCategories;
          delete reqBody.allToppingCategoryIds;
          delete reqBody.isBestSeller;
          delete reqBody.isTrending;
          delete reqBody.badges;
          delete reqBody.bestSellerScore;
          delete reqBody.trendingScore;
          delete reqBody.badgesUpdatedAt;
          reqBody.shapeItems = reqBody.shapeItems.filter(
            (item: ImageItems) => item.top && item.front
          );
          await dispatch(patchToppingsCategoryItem(reqBody)).unwrap();
        }
        formik.resetForm();
        dispatch(setStatus({ status: 'idle' }));
        dispatch(resetSelectToppingsCategoryItemReset());
        dispatch(selectToppingsCategoryItemSetStatus({ status: 'idle' }));
        navigation('/toppings-category-item');
      } catch (error: any) {}
    },
  });

  useEffect(() => {
    const fetchById = async () => {
      if (isAddMode) return;

      if (state) {
        formik.setValues(fillToppingCategoryItemState(state));
        setResponse(state);
      } else {
        try {
          const decodeParam = decryptString(params.id);
          const record = await dispatch(
            getToppingsCategoryItem({ id: decodeParam })
          ).unwrap();
          formik.setValues(fillToppingCategoryItemState(record));
          setResponse(record);

          // Reconstruct allToppingCategories from response data
          const primaryCategory = record.topping_category;
          const additionalCats = record.additionalCategories?.map(
            (ac: any) => ac.toppingCategory
          ) || [];
          if (primaryCategory) {
            setAllToppingCategories([primaryCategory, ...additionalCats]);
          }
        } catch (error: any) {
          navigation('/toppings-category-item');
        }
      }
    };
    fetchById();
  }, [dispatch, isAddMode, navigation, params?.id, formik.setValues, state]);

  // Topping Category Data
  const {
    data: toppingCategoryData,
    isLoading: isToppingCategoryLoading,
    skip: toppingCategorySkip,
    hasMore: toppingCategoryHasMore,
    status: toppingCategoryStatus,
  } = useAppSelector((state) => state.selectToppingsCategory);

  useEffect(() => {
    if (toppingCategoryStatus === 'idle') {
      dispatch(
        findSelectToppingsCategory({
          limit: 10,
          skip: toppingCategorySkip,
        })
      );
    }
  }, [toppingCategorySkip, toppingCategoryStatus]);

  const onScrollFetchToppingsCategory = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search?: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      toppingCategoryHasMore
    ) {
      dispatch(setSkipToppingsCategory({ skip: toppingCategorySkip + 10 }));
      dispatch(
        findSelectToppingsCategory({
          limit: 10,
          skip: toppingCategorySkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchToppingsCategory = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetToppingsCategory());
      return;
    }
    const params = {
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipToppingsCategory({ skip: 0 }));
    dispatch(searchSelectToppingsCategory(params));
  };

  const uniqueToppingCategoryData = uniqBy(
    [
      ...(response?.topping_category ? [response?.topping_category] : []),
      ...(response?.additionalCategories
        ? response.additionalCategories.map((ac: any) => ac.toppingCategory).filter(Boolean)
        : []),
      ...toppingCategoryData,
    ],
    'id'
  );

  // Reconstruct allToppingCategories for edit mode when data loads
  useEffect(() => {
    if (
      !isAddMode &&
      formik.values.toppingCategoryId &&
      uniqueToppingCategoryData?.length > 0 &&
      !hasTriggeredRefresh.current
    ) {
      const primaryCategory = uniqueToppingCategoryData.find(
        (cat: any) => cat.id === formik.values.toppingCategoryId
      );
      const additionalCats = uniqueToppingCategoryData.filter((cat: any) =>
        formik.values.additionalToppingCategoryIds?.includes(cat.id)
      );

      if (primaryCategory) {
        setAllToppingCategories([primaryCategory, ...additionalCats]);
        hasTriggeredRefresh.current = true;
      }
    }
  }, [
    isAddMode,
    formik.values.toppingCategoryId,
    formik.values.additionalToppingCategoryIds,
    uniqueToppingCategoryData,
  ]);

  // Reset when switching modes
  useEffect(() => {
    hasTriggeredRefresh.current = false;
    if (isAddMode) {
      setAllToppingCategories([]);
    }
  }, [isAddMode]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    field: string
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        documentType,
        accessToken
      );
      if (uploadResult?.data?.id) {
        formik?.setFieldValue(field, uploadResult?.data?.keypath);
      }
    }
  };

  // Build dropdown options excluding already selected categories
  const dropdownOptions = uniqueToppingCategoryData.filter(
    (cat: any) => !allToppingCategories.some((selected: any) => selected.id === cat.id)
  );

  return (
    <Grid
      container
      spacing={3}
    >
      <Grid
        item
        xs={12}
        md={12}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
              <Card sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    },
                  }}
                >
                  <TextField
                    fullWidth
                    label={'Label English'}
                    name='labelEn'
                    id='labelEn'
                    onChange={formik.handleChange}
                    value={formik.values.labelEn}
                    error={Boolean(
                      formik.errors.labelEn && formik.touched.labelEn
                    )}
                    helperText={
                      Boolean(
                        formik.errors.labelEn && formik.touched.labelEn
                      ) && formik.errors.labelEn
                    }
                  />
                  <TextField
                    fullWidth
                    label={'Label Arabic'}
                    name='labelAr'
                    id='labelAr'
                    onChange={formik.handleChange}
                    value={formik.values.labelAr}
                    error={Boolean(
                      formik.errors.labelAr && formik.touched.labelAr
                    )}
                    helperText={
                      Boolean(
                        formik.errors.labelAr && formik.touched.labelAr
                      ) && formik.errors.labelAr
                    }
                  />

                  <MultipleAutoComplete
                    label='Select Topping Categories'
                    options={dropdownOptions}
                    loading={isToppingCategoryLoading}
                    value={allToppingCategories}
                    key={`topping-categories-${allToppingCategories.length}-${formik.values.toppingCategoryId}`}
                    limitTags={3}
                    onChange={(e, value) => {
                      // Remove duplicates based on category ID
                      const uniqueCategories: any[] = [];
                      const seenIds = new Set<string>();

                      for (const cat of value) {
                        if (!seenIds.has(cat.id)) {
                          seenIds.add(cat.id);
                          uniqueCategories.push(cat);
                        }
                      }

                      setAllToppingCategories(uniqueCategories);

                      if (uniqueCategories && uniqueCategories.length > 0) {
                        formik.setFieldValue(
                          'toppingCategoryId',
                          uniqueCategories[0].id
                        );
                        const additionalIds = uniqueCategories
                          .slice(1)
                          .map((v: any) => v.id);
                        formik.setFieldValue(
                          'additionalToppingCategoryIds',
                          additionalIds
                        );
                      } else {
                        setAllToppingCategories([]);
                        formik.setFieldValue('toppingCategoryId', '');
                        formik.setFieldValue(
                          'additionalToppingCategoryIds',
                          []
                        );
                      }
                    }}
                    onInputChange={(_event, _newInputValue) => {
                      // Handle input change for search
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>{option.labelEn}</li>
                    )}
                    getOptionLabel={(option) => option.labelEn || ''}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option.id === value.id
                    }
                    fetchFunction={(e) => onScrollFetchToppingsCategory(e)}
                    searchFunction={searchToppingsCategory}
                    error={Boolean(
                      formik.touched.toppingCategoryId &&
                        formik.errors.toppingCategoryId
                    )}
                    helperText={
                      Boolean(
                        formik.touched.toppingCategoryId &&
                          formik.errors.toppingCategoryId
                      )
                        ? (formik.errors.toppingCategoryId as string)
                        : 'First selected category is the primary category'
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Code'}
                    name='code'
                    id='code'
                    onChange={formik.handleChange}
                    value={formik.values.code}
                    error={Boolean(formik.errors.code && formik.touched.code)}
                    helperText={
                      Boolean(formik.errors.code && formik.touched.code) &&
                      formik.errors.code
                    }
                  />

                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={formik?.values?.previewUrl ? 8.5 : 12}
                      sm={formik?.values?.previewUrl ? 8.5 : 12}
                    >
                      <TextField
                        type='file'
                        fullWidth
                        name='previewUrl'
                        id='previewUrl'
                        onChange={(e) =>
                          handleFileChange(
                            e as React.ChangeEvent<HTMLInputElement>,
                            DocumentType.Topping,
                            'previewUrl'
                          )
                        }
                        inputProps={{ multiple: false, accept: 'image/*' }}
                        InputLabelProps={{ shrink: true }}
                        variant='outlined'
                        label={'Preview URL'}
                        error={Boolean(
                          formik?.errors.previewUrl &&
                            formik?.touched.previewUrl
                        )}
                        helperText={
                          Boolean(
                            formik?.errors.previewUrl &&
                              formik?.touched.previewUrl
                          ) && formik?.errors.previewUrl
                        }
                      />
                    </Grid>
                    {formik?.values?.previewUrl && (
                      <Grid
                        item
                        xs={3.5}
                        sm={3.5}
                      >
                        <Avatar
                          imgProps={{ crossOrigin: 'anonymous' }}
                          src={getAssetURl(
                            formik?.values?.previewUrl,
                            assetUrl
                          )}
                          sx={{
                            borderRadius: 'unset',
                            m: 1,
                            objectFit: 'contain',
                            '& img': { objectFit: 'contain' },
                            width: { xs: '75px', sm: '100px' },
                            height: 50,
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  <TextField
                    fullWidth
                    label={'Position'}
                    name='position'
                    id='position'
                    onChange={formik.handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={formik.values.position}
                    error={Boolean(
                      formik.errors.position && formik.touched.position
                    )}
                    helperText={
                      Boolean(
                        formik.errors.position && formik.touched.position
                      ) && formik.errors?.position
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Price'}
                    name='price'
                    id='price'
                    onChange={formik.handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={formik.values.price}
                    error={Boolean(formik.errors.price && formik.touched.price)}
                    helperText={
                      Boolean(formik.errors.price && formik.touched.price) &&
                      formik.errors?.price
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Discounted Price'}
                    name='discountedPrice'
                    id='discountedPrice'
                    onChange={formik.handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={formik.values.discountedPrice}
                    error={Boolean(
                      formik.errors.discountedPrice &&
                        formik.touched.discountedPrice
                    )}
                    helperText={
                      Boolean(
                        formik.errors.discountedPrice &&
                          formik.touched.discountedPrice
                      ) && formik.errors?.discountedPrice
                    }
                  />

                </Box>

                <Box sx={{ pt: 2.5 }}>
                  <TextField
                    fullWidth
                    label={'English Description'}
                    name='descriptionEn'
                    id='descriptionEn'
                    onChange={formik.handleChange}
                    value={formik.values.descriptionEn}
                    error={Boolean(
                      formik.errors.descriptionEn &&
                        formik.touched.descriptionEn
                    )}
                    helperText={
                      Boolean(
                        formik.errors.descriptionEn &&
                          formik.touched.descriptionEn
                      ) && formik.errors.descriptionEn
                    }
                  />
                </Box>

                <Box sx={{ pt: 2.5, pb: 2.5 }}>
                  <TextField
                    fullWidth
                    label={'Arabic Description'}
                    name='descriptionAr'
                    id='descriptionAr'
                    onChange={formik.handleChange}
                    value={formik.values.descriptionAr}
                    error={Boolean(
                      formik.errors.descriptionAr &&
                        formik.touched.descriptionAr
                    )}
                    helperText={
                      Boolean(
                        formik.errors.descriptionAr &&
                          formik.touched.descriptionAr
                      ) && formik.errors.descriptionAr
                    }
                  />
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    },
                  }}
                >
                  <TextField
                    fullWidth
                    label={'Delivery Gap'}
                    name='deliveryDayGap'
                    id='deliveryDayGap'
                    onChange={formik?.handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={formik?.values.deliveryDayGap}
                    error={Boolean(
                      formik?.errors.deliveryDayGap &&
                        formik?.touched.deliveryDayGap
                    )}
                    helperText={
                      Boolean(
                        formik?.errors.deliveryDayGap &&
                          formik?.touched.deliveryDayGap
                      ) && formik?.errors?.deliveryDayGap
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Effort Score'}
                    name='effortScore'
                    id='effortScore'
                    onChange={formik.handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={formik.values.effortScore}
                    error={Boolean(
                      formik.errors.effortScore && formik.touched.effortScore
                    )}
                    helperText={
                      (Boolean(
                        formik.errors.effortScore && formik.touched.effortScore
                      ) &&
                        formik.errors?.effortScore) ||
                      'Delivery slot capacity points per cake (default 10)'
                    }
                  />
                </Box>

                <Grid
                  container
                  spacing={2}
                  py={2}
                >
                  <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          color='primary'
                          name='activeState'
                          onChange={(e) =>
                            formik.setFieldValue(
                              'activeState',
                              e.target.checked
                            )
                          }
                          checked={formik.values.activeState}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                        />
                      }
                      label={'Active Status'}
                    />
                  </Grid>
                  {/* <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          color='primary'
                          name='isNew'
                          onChange={(e) =>
                            formik.setFieldValue('isNew', e.target.checked)
                          }
                          checked={formik.values.isNew}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                        />
                      }
                      label={'New'}
                    />
                  </Grid> */}
                  <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          color='primary'
                          name='hasColor'
                          onChange={(e) =>
                            formik.setFieldValue('hasColor', e.target.checked)
                          }
                          checked={formik.values.hasColor}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                        />
                      }
                      label={'Has Color'}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          color='primary'
                          name='isBig'
                          onChange={(e) =>
                            formik.setFieldValue('isBig', e.target.checked)
                          }
                          checked={formik.values.isBig}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                        />
                      }
                      label={'Big'}
                    />
                  </Grid>
                </Grid>

                <DetailsForm
                  isAddMode={isAddMode}
                  response={response}
                  values={formik.values}
                  handleChange={formik.handleChange}
                  setFieldValue={formik.setFieldValue}
                  errors={formik.errors}
                  touched={formik.touched}
                />

                <Stack
                  alignItems='center'
                  sx={{ mt: 3 }}
                  direction='row'
                  justifyContent='flex-end'
                  gap={1}
                >
                  <Button
                    variant='outlined'
                    onClick={() => navigation('/toppings-category-item')}
                  >
                    {'Cancel'}
                  </Button>

                  <LoadingButton
                    type='submit'
                    variant='contained'
                    loading={formik.isSubmitting}
                  >
                    {isAddMode
                      ? 'Add Toppings Category Item'
                      : 'Edit Toppings Category Item'}
                  </LoadingButton>
                </Stack>
              </Card>
            </form>
          </FormikProvider>
        )}
      </Grid>
    </Grid>
  );
};

export default FlavorForm;
